import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Typography, Collapse, Button, Space, Divider } from 'antd'
import MaterialIcon from '../components/MaterialIcon'
import { getWebPortalUrl } from '../config/runtimeEnv'
import { useSSRBaseUrl, useSSRWebPortalUrl } from '../ssr/SSRContext'

const { Title, Paragraph, Text } = Typography

interface FaqItem {
  question: string
  answer: string
}

const GMAIL_FAQS: FaqItem[] = [
  {
    question: 'What is Gmail for Business?',
    answer: 'Gmail for Business is ServiceFlow Pro\'s integrated email experience. It brings your Gmail inbox into one place with your customers, jobs, and estimates—with AI-powered responses, bulk campaigns, and analytics so you can respond faster and close more deals.'
  },
  {
    question: 'How does Gmail for Business help service-based industries?',
    answer: 'Service companies (HVAC, plumbing, electrical, garage door, etc.) get a unified inbox, AI reply suggestions, automatic linking of emails to customers and jobs, price book insertion in emails, and the ability to create estimates and jobs from threads. That reduces back-and-forth and keeps everything in one system.'
  },
  {
    question: 'Do I need a separate Gmail account?',
    answer: 'You use your existing Google Workspace or Gmail account. Connect it once in ServiceFlow Pro (Settings → Integrations). After that, you access and send mail from the Gmail portal without leaving the app.'
  },
  {
    question: 'Is my email data secure?',
    answer: 'Yes. We use Google\'s OAuth and only access the scopes you approve. Emails are processed in line with our privacy policy and security practices. You can disconnect Gmail at any time from Integrations.'
  },
  {
    question: 'Can I send bulk emails to customers?',
    answer: 'Yes. You can run targeted email campaigns to segments of your customer base, track opens and clicks, and keep templates for common messages—all from the Gmail portal.'
  },
  {
    question: 'How does AI response suggestion work?',
    answer: 'When you open a thread, the system uses the conversation and linked customer/job data to suggest reply text. You can edit and send as-is or use it as a starting point. It’s designed to save time while keeping your tone and accuracy.'
  },
  {
    question: 'How does this connect to ServiceFlow Pro?',
    answer: 'Gmail for Business is part of ServiceFlow Pro. Your inbox is linked to the same customers, jobs, estimates, and price book. Create estimates or jobs from emails, insert price book items into replies, and keep full context without switching apps.'
  },
  {
    question: 'Where do I get started?',
    answer: 'Log in at the main ServiceFlow Pro portal, go to Settings → Integrations, and connect Gmail. Then open the Gmail portal from the same menu or use the direct link. You’ll see your inbox and can start using AI suggestions and customer linking right away.'
  }
]

const FAQ_TITLE = 'Gmail for Business – FAQ | ServiceFlow Pro'
const FAQ_DESCRIPTION = 'Frequently asked questions about Gmail for Business: unified inbox, AI responses, and email efficiency for service companies.'

const FaqPage: React.FC = () => {
  const baseUrl = useSSRBaseUrl() || (typeof window !== 'undefined' ? window.location.origin : 'https://gmail.serviceflow-pro.com')
  const ssrWebPortalUrl = useSSRWebPortalUrl()
  const webPortalUrl = ssrWebPortalUrl || getWebPortalUrl()
  const productUrl = `${webPortalUrl}/product`

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: GMAIL_FAQS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  }

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl.replace(/\/$/, '') },
      { '@type': 'ListItem', position: 2, name: 'FAQ', item: `${baseUrl.replace(/\/$/, '')}/faq` }
    ]
  }

  return (
    <>
      <Helmet>
        <title>{FAQ_TITLE}</title>
        <meta name="description" content={FAQ_DESCRIPTION} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${baseUrl}/faq`} />
        <meta property="og:title" content={FAQ_TITLE} />
        <meta property="og:description" content={FAQ_DESCRIPTION} />
        <meta property="og:url" content={`${baseUrl}/faq`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ padding: '48px 24px 60px', maxWidth: 800, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <MaterialIcon icon="help" style={{ fontSize: 48, color: 'white', marginBottom: 16 }} />
            <Title level={1} style={{ color: 'white', marginBottom: 8 }}>
              Frequently Asked Questions
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem' }}>
              Gmail for Business – ServiceFlow Pro
            </Text>
          </div>

          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)'
          }}>
            <Collapse
              accordion
              defaultActiveKey={['0']}
              style={{ border: 'none', background: 'transparent' }}
              items={GMAIL_FAQS.map((item, index) => ({
                key: String(index),
                label: <Text strong style={{ fontSize: '1rem' }}>{item.question}</Text>,
                children: <Paragraph style={{ marginBottom: 0, color: '#374151' }}>{item.answer}</Paragraph>
              }))}
            />
          </div>

          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Paragraph style={{ color: 'rgba(255,255,255,0.9)', marginBottom: 16 }}>
              Still have questions?
            </Paragraph>
            <Space wrap>
              <Button
                type="primary"
                size="large"
                icon={<MaterialIcon icon="home" />}
                href="/"
                style={{ background: 'white', color: '#667eea', border: 'none' }}
              >
                Back to Gmail for Business
              </Button>
              <Button
                size="large"
                icon={<MaterialIcon icon="open_in_new" />}
                href={productUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '2px solid white' }}
              >
                ServiceFlow Pro Product
              </Button>
            </Space>
          </div>
        </div>

        <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.75)' }}>
          <Space split={<Divider type="vertical" style={{ borderColor: 'rgba(255,255,255,0.3)' }} />}>
            <a href="/" style={{ color: 'inherit' }}>Home</a>
            <a href="/faq" style={{ color: 'inherit' }}>FAQ</a>
            <a href={productUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
              ServiceFlow Pro
            </a>
          </Space>
        </div>
      </div>
    </>
  )
}

export default FaqPage
